import React, { FC, ReactElement, ReactNode, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles, Theme } from '@material-ui/core/styles';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import useStore from 'store';
import { Locale } from 'types/schema-generated-types';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';
import { LocaleStudentSettingProps } from '../../../../types';

type CommonLocaleSettingProps = LocaleStudentSettingProps & {
  onRemove: () => Promise<any>;
  onUpdate: (value: string) => Promise<any>;
  description: string;
  formLabel?: ReactNode;
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    },
    label: {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
      lineHeight: theme.typography.pxToRem(24),
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(-1.3),
      '&.Mui-focused': {
        color: `${theme.palette.common.black} !important`
      }
    }
  };
});

const getClassroomlevelSetting = (classroomSettingValue: string | null) => {
  const { label } =
    LANGUAGE_OPTIONS.find(x => x.value === classroomSettingValue) ?? LANGUAGE_OPTIONS[0];

  return [{ label: `Use Class Level Setting - ${label}`, value: '' }];
};

const LANGUAGE_OPTIONS = [
  {
    label: 'English',
    value: Locale.EN_US
  },
  {
    label: 'Español',
    value: Locale.ES_US
  }
];

const LocaleSetting: FC<CommonLocaleSettingProps> = ({
  setting,
  classroomSetting,
  expanded,
  handleChangeExpanded,
  panel,
  onRemove,
  onUpdate,
  description,
  formLabel
}) => {
  const { t } = useTranslation('LanguageOptionSetting');
  const classes = useStyles();
  const [, { openAlert }] = useStore();

  const [value, setValue] = useState(setting || '');
  const [classroomSettingValue] = useState(classroomSetting || '');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setValue((event.target as HTMLInputElement).value as Locale);
  };

  const handleOnCancel = (): void => setValue(setting || '');

  const handleOnConfirm = async (): Promise<void> => {
    try {
      if (value === '') await onRemove();
      else await onUpdate(value);
      /*
      await removeMutation({
          variables: {
            where: {
              studentId,
              appName,
              setting: 'locale'
            }
          }
        });
      } else {
        await updateMutation({
          variables: {
            data: {
              studentId,
              value
            }
          }
        });
      }
      */
      openAlert(getSettingSuccessAlert('Language', t));
    } catch (error) {
      console.log('Error');
    }
  };

  const languageOptions = useMemo(
    () => [...getClassroomlevelSetting(classroomSettingValue), ...LANGUAGE_OPTIONS],
    [classroomSettingValue]
  );

  return (
    <SettingsCard
      title={t('Language Option')}
      description={description}
      expanded={expanded}
      panel={panel}
      handleChangeExpanded={handleChangeExpanded}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        {formLabel && (
          <FormLabel component="label" className={classes.label}>
            {formLabel}
          </FormLabel>
        )}
        <RadioGroup
          aria-label="language setting"
          name="languageSetting"
          value={value}
          onChange={handleChange}
        >
          {languageOptions.map(
            (x: any, key): ReactElement => (
              <FormControlLabel
                key={`language-option-${key}`}
                value={x.value}
                control={<Radio />}
                label={x.label}
              />
            )
          )}
        </RadioGroup>
      </FormControl>
    </SettingsCard>
  );
};

export default LocaleSetting;
